import { LocalStorageKey } from '@alsid/common/types/environment'
import { match } from 'ts-pattern'
import { type ExportCsvSeparator, ExportCsvSeparatorEnum } from './types'

/**
 * Sanitize str for CSV exports.
 */
export function sanitizeCsvValue(str: unknown): string {
  const dangerousChars = new Set(['=', '-', '"', '@', '+', '	'])

  if (typeof str === 'number' || typeof str === 'bigint') {
    return String(str)
  }

  if (!str) {
    return ''
  }

  let csvValue = String(str)

  /**
   * @see https://wiki.mozilla.org/images/6/6f/Phpmyadmin-report.pdf
   *
   * When performing a CSV Export, for any cell that starts with an =, -, ", @, or +, add a space to
   * the beginning and remove any tab characters (0x09) in the cell
   */
  if (dangerousChars.has(String(csvValue.at(0)))) {
    csvValue = ` ${csvValue.replaceAll(/\t/g, ' ')}`
  }

  /**
   * @see https://www.ietf.org/rfc/rfc4180.txt
   *
   * Fields containing line breaks (CRLF), double quotes, and commas
   * should be enclosed in double-quotes.
   *
   * If double-quotes are used to enclose fields, then a double-quote
   * appearing inside a field must be escaped by preceding it with
   * another double quote.
   */
  return `"${csvValue.replaceAll('"', '""')}"`
}

/**
 * Default separator set to semicolon to keep compatibility with the previous behavior
 * when separator were not configurable in exports/reports.
 */
export const DEFAULT_CSV_SEPARATOR = ExportCsvSeparatorEnum.semicolon

/**
 * Return the separator character from the union value.
 */
export function getSeparatorCharacter(separator?: ExportCsvSeparator): string {
  return match(separator ?? DEFAULT_CSV_SEPARATOR)
    .with('comma', () => ',')
    .with('semicolon', () => ';')
    .otherwise(() => ';')
}

/**
 * Return the separator character from the union value.
 */
export function ensureValidExportCsvSeparator(
  separator?: any
): ExportCsvSeparator {
  return match(separator ?? DEFAULT_CSV_SEPARATOR)
    .with('comma', () => ExportCsvSeparatorEnum.comma)
    .with('semicolon', () => ExportCsvSeparatorEnum.semicolon)
    .otherwise(() => ExportCsvSeparatorEnum.semicolon)
}

/**
 * Get/set the default CSV separator from the storage.
 */
export function csvSeparatorStorage(storage: Storage) {
  const isSeparatorValid = (
    csvSeparator: any
  ): csvSeparator is ExportCsvSeparatorEnum => {
    return Object.values(ExportCsvSeparatorEnum)
      .map(String)
      .includes(csvSeparator as LocalStorageKey)
  }

  return {
    set: (csvSeparator: ExportCsvSeparator): ExportCsvSeparatorEnum => {
      const finalCsvSeparator = isSeparatorValid(csvSeparator)
        ? csvSeparator
        : DEFAULT_CSV_SEPARATOR

      storage.setItem(LocalStorageKey.tadDefaultCsvSeparator, finalCsvSeparator)

      return finalCsvSeparator
    },

    get: (): ExportCsvSeparatorEnum => {
      const currentCsvSeparator = storage.getItem(
        LocalStorageKey.tadDefaultCsvSeparator
      )

      if (!isSeparatorValid(currentCsvSeparator)) {
        storage.setItem(
          LocalStorageKey.tadDefaultCsvSeparator,
          DEFAULT_CSV_SEPARATOR
        )

        return DEFAULT_CSV_SEPARATOR
      }

      return currentCsvSeparator
    }
  }
}
